/**
 * Departments, and who approves for each.
 *
 * The approval authority is not the department head field: it is a dated
 * assignment, because "who approved for Operations on 3 September" has to
 * stay answerable after the role changes hands. A change ends the current
 * assignment and starts the next; nothing is edited in place.
 *
 * The list says who approves for each department today; a department opens
 * beside it with its details, the history of its authority, and the forms
 * that name the next one or end the current one.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type Authority, type Department, type User } from '../api'
import { useAuth } from '../auth'
import { ErrorNote, Field, Loading, Note, Stat } from '../components/ui'
import { Pager, usePaged } from '../components/Pager'
import { SidePanel, Tabs, matches, rowProps } from '../components/SidePanel'

function today(): string {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

type Tab = 'active' | 'inactive' | 'all'

export default function Departments() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const editable = can('org.department.configure')

  const { data, isLoading, error } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/org/departments'),
  })
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<User[]>('/org/users'),
    enabled: can('org.user.view'),
  })

  const [form, setForm] = useState({ code: '', name: '' })
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [tab, setTab] = useState<Tab>('active')
  const [search, setSearch] = useState('')
  const [onlyMissing, setOnlyMissing] = useState(false)

  const create = useMutation({
    mutationFn: () => api.post<Department>('/org/departments', form),
    onSuccess: (made) => {
      setForm({ code: '', name: '' })
      setAdding(false)
      void queryClient.invalidateQueries({ queryKey: ['departments'] })
      // Straight to it: the next thing anybody does is name who approves.
      setOpen(made.id)
    },
  })

  const rows = data ?? []
  const userName = (id: string | null) =>
    (users.data ?? []).find((u) => u.id === id)?.display_name ?? null
  const shown = rows.filter(
    (r) =>
      (tab === 'all' || (tab === 'active' ? r.is_active : !r.is_active)) &&
      (!onlyMissing || !r.authority_user_id) &&
      matches(search, r.code, r.name, r.authority_name, userName(r.head_user_id)),
  )
  const paged = usePaged(shown, `${tab}|${search}|${onlyMissing}`)

  if (isLoading) return <Loading what="Loading departments" />
  const opened = rows.find((r) => r.id === open)
  const active = rows.filter((r) => r.is_active)
  const withoutAuthority = active.filter((r) => !r.authority_user_id)
  const approvers = new Set(active.map((r) => r.authority_user_id).filter(Boolean))

  return (
    <>
      <ErrorNote error={error} />

      {editable && (
        <div className="bank-toolbar">
          <button className="primary" onClick={() => setAdding(true)}>
            Add a department
          </button>
        </div>
      )}

      {withoutAuthority.length > 0 && (
        <Note kind="warn">
          {withoutAuthority.map((d) => d.name).join(', ')}{' '}
          {withoutAuthority.length === 1 ? 'has' : 'have'} no approval authority. Anything whose
          approval rule includes a department step cannot be submitted for{' '}
          {withoutAuthority.length === 1 ? 'it' : 'them'} until one is named.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Active departments"
          value={String(active.length)}
          note={`${rows.length - active.length} inactive`}
        />
        <Stat
          label="Without an approval authority"
          value={String(withoutAuthority.length)}
          note={
            withoutAuthority.length
              ? 'department-step requests cannot be submitted for these'
              : 'every active department has one'
          }
        />
        <Stat
          label="People approving"
          value={String(approvers.size)}
          note="confirm the need; the Finance Head approves the money"
        />
        <Stat
          label="With a department head"
          value={String(active.filter((r) => r.head_user_id).length)}
          note="the organisation chart; does not approve"
        />
      </div>

      <section className="card wide">
        <header className="appr-toolbar">
          <Tabs
            tabs={[
              { key: 'active', label: 'Active', count: active.length },
              {
                key: 'inactive',
                label: 'Inactive',
                count: rows.length - active.length,
                quietWhenZero: true,
              },
              { key: 'all', label: 'All', count: rows.length },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="appr-filters">
            <input
              type="search"
              placeholder="Code, name, approver, head"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search departments"
            />
            <label className="appr-check">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => setOnlyMissing(e.target.checked)}
              />
              only without an approver
            </label>
          </div>
        </header>
        <div className="body tight">
          {shown.length === 0 ? (
            <div className="empty">
              {rows.length === 0
                ? 'No departments yet. Spend with no department shows as “Unassigned” on reports, which is a reported state rather than an error — but it is not much of a report.'
                : 'No department matches that.'}
            </div>
          ) : (
            <table className="appr-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Approval authority</th>
                  <th>Department head</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {paged.rows.map((r) => (
                  <tr key={r.id} {...rowProps(() => setOpen(r.id), open === r.id)}>
                    <td>
                      <div className="strong">{r.name}</div>
                      <div className="cell-sub ref">{r.code}</div>
                    </td>
                    <td>
                      {r.authority_name ? (
                        <>
                          {r.authority_name}
                          <div className="cell-sub">since {r.authority_since}</div>
                        </>
                      ) : (
                        <span className="pill advisory">none named</span>
                      )}
                    </td>
                    <td className="muted">
                      {userName(r.head_user_id) ?? <span className="faint">—</span>}
                    </td>
                    <td>
                      <span className={`pill ${r.is_active ? 'approved' : 'draft'}`}>
                        {r.is_active ? 'active' : 'inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} />
      </section>

      {opened && (
        <SidePanel
          kicker="Department"
          title={opened.name}
          sub={
            <>
              <span className="ref">{opened.code}</span> ·{' '}
              {opened.authority_name
                ? `${opened.authority_name} approves, since ${opened.authority_since}`
                : 'nobody approves for it yet'}
            </>
          }
          badge={
            <span className={`pill ${opened.authority_user_id ? 'approved' : 'advisory'}`}>
              {opened.authority_user_id ? 'has an approver' : 'needs an approver'}
            </span>
          }
          wide
          onClose={() => setOpen(null)}
        >
          <DepartmentPanel
            key={opened.id}
            department={opened}
            users={users.data ?? []}
            editable={editable}
            canAssign={can('org.approval_authority.configure')}
            canSeeAuthorities={can('org.approval_authority.view')}
          />
        </SidePanel>
      )}

      {adding && editable && (
        <SidePanel
          kicker="Department"
          title="Add a department"
          sub="the second reporting axis; name who approves for it next"
          onClose={() => setAdding(false)}
        >
          <ErrorNote error={create.error} />
          <form
            onSubmit={(e) => {
              e.preventDefault()
              create.mutate()
            }}
          >
            <Field label="Code">
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                required
                maxLength={20}
              />
            </Field>
            <Field label="Name">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Field>
            <div className="row" style={{ marginTop: 12 }}>
              <div className="shrink">
                <button className="primary" type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Adding…' : 'Add department'}
                </button>
              </div>
            </div>
          </form>
        </SidePanel>
      )}
    </>
  )
}

function DepartmentPanel({
  department,
  users,
  editable,
  canAssign,
  canSeeAuthorities,
}: {
  department: Department
  users: User[]
  editable: boolean
  canAssign: boolean
  canSeeAuthorities: boolean
}) {
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['departments'] })
    void queryClient.invalidateQueries({ queryKey: ['authorities', department.id] })
  }

  const history = useQuery({
    queryKey: ['authorities', department.id],
    queryFn: () => api.get<Authority[]>(`/org/departments/${department.id}/approval-authorities`),
    enabled: canSeeAuthorities,
  })

  const [details, setDetails] = useState({
    name: department.name,
    is_active: department.is_active,
    head_user_id: department.head_user_id ?? '',
  })
  const save = useMutation({
    mutationFn: () =>
      api.put<Department>(`/org/departments/${department.id}`, {
        name: details.name,
        is_active: details.is_active,
        sort_order: department.sort_order,
        head_user_id: details.head_user_id || null,
      }),
    onSuccess: refresh,
  })

  // Who can hold the capability: Employees (department heads sign in as
  // Employees) and Finance Heads. The backend refuses anyone else too.
  const candidates = users.filter(
    (u) => u.is_active && (u.role === 'employee' || u.role === 'finance_head'),
  )
  const [assign, setAssign] = useState({ user_id: '', effective_from: today(), reason: '' })
  const assignMutation = useMutation({
    mutationFn: () =>
      api.post<Authority>(`/org/departments/${department.id}/approval-authorities`, assign),
    onSuccess: () => {
      setAssign({ user_id: '', effective_from: today(), reason: '' })
      refresh()
    },
  })

  const current = (history.data ?? []).find((a) => a.is_current)
  const [ending, setEnding] = useState({ last_day: today(), reason: '' })
  const endMutation = useMutation({
    mutationFn: () => api.post<Authority>(`/org/approval-authorities/${current!.id}/end`, ending),
    onSuccess: () => {
      setEnding({ last_day: today(), reason: '' })
      refresh()
    },
  })

  const headName = users.find((u) => u.id === department.head_user_id)?.display_name

  return (
    <>
      <section>
        <dl className="appr-facts">
          <div>
            <dt>Approves today</dt>
            <dd>{department.authority_name ?? 'nobody'}</dd>
          </div>
          <div>
            <dt>Department head</dt>
            <dd>{headName ?? '—'}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>
              <span className={`pill ${department.is_active ? 'approved' : 'draft'}`}>
                {department.is_active ? 'active' : 'inactive'}
              </span>
            </dd>
          </div>
        </dl>
      </section>

      {editable && (
        <section>
          <h3>Details</h3>
          <ErrorNote error={save.error} />
          {save.isSuccess && <Note kind="ok">Saved.</Note>}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            <div className="row">
              <Field label="Name">
                <input
                  value={details.name}
                  onChange={(e) => setDetails({ ...details, name: e.target.value })}
                  required
                />
              </Field>
              <Field label="Department head" caption="the organisation chart; does not approve">
                <select
                  value={details.head_user_id}
                  onChange={(e) => setDetails({ ...details, head_user_id: e.target.value })}
                >
                  <option value="">—</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Active">
                <select
                  value={details.is_active ? 'yes' : 'no'}
                  onChange={(e) => setDetails({ ...details, is_active: e.target.value === 'yes' })}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </Field>
              <div className="shrink">
                <button className="primary" type="submit" disabled={save.isPending}>
                  Save
                </button>
              </div>
            </div>
          </form>
        </section>
      )}

      {canSeeAuthorities && (
        <section>
          <h3>
            Approval authority{' '}
            <span className="hint">
              · confirms the department genuinely needs something; the Finance Head approves the
              money
            </span>
          </h3>
          <ErrorNote error={history.error} />
          {(history.data ?? []).length === 0 ? (
            <div className="faint">Nobody has approved for this department yet.</div>
          ) : (
            <ol className="appr-route">
              {(history.data ?? []).map((a) => (
                <li key={a.id} className={a.is_current ? 'now' : 'done'}>
                  <div className="route-head">
                    <strong>{a.user_name}</strong>
                    {a.is_current && <span className="pill pass">current</span>}
                    <span className="muted date">
                      {a.effective_from} → {a.effective_to ?? 'open'}
                    </span>
                  </div>
                  <div className="muted">
                    {a.reason}
                    {a.configured_by_name && ` · set by ${a.configured_by_name}`}
                  </div>
                  {a.ended_reason && <div className="faint">ended: {a.ended_reason}</div>}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {canAssign && (
        <section>
          <h3>
            Name the approval authority{' '}
            <span className="hint">· from today or a later date; never backdated</span>
          </h3>
          <ErrorNote error={assignMutation.error} />
          <Note kind="info">
            Requests already submitted stay with whoever they were sent to. From the start date, new
            requests for {department.name} go to the person named here. Starting a new assignment
            ends the current one the day before.
          </Note>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              assignMutation.mutate()
            }}
          >
            <div className="row">
              <Field label="Person" caption="an Employee or a Finance Head">
                <select
                  value={assign.user_id}
                  onChange={(e) => setAssign({ ...assign, user_id: e.target.value })}
                  required
                >
                  <option value="">Choose…</option>
                  {candidates.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.display_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="From">
                <input
                  type="date"
                  value={assign.effective_from}
                  min={today()}
                  onChange={(e) => setAssign({ ...assign, effective_from: e.target.value })}
                  required
                />
              </Field>
            </div>
            <Field label="Why" caption="kept in the audit trail">
              <input
                value={assign.reason}
                onChange={(e) => setAssign({ ...assign, reason: e.target.value })}
                required
                minLength={3}
              />
            </Field>
            <button className="primary" type="submit" disabled={assignMutation.isPending}>
              Name as approval authority
            </button>
          </form>
        </section>
      )}

      {canAssign && current && (
        <section>
          <h3>End {current.user_name}’s assignment</h3>
          <ErrorNote error={endMutation.error} />
          <form
            onSubmit={(e) => {
              e.preventDefault()
              endMutation.mutate()
            }}
          >
            <div className="row">
              <Field label="Last day">
                <input
                  type="date"
                  value={ending.last_day}
                  min={today()}
                  onChange={(e) => setEnding({ ...ending, last_day: e.target.value })}
                  required
                />
              </Field>
              <Field label="Why">
                <input
                  value={ending.reason}
                  onChange={(e) => setEnding({ ...ending, reason: e.target.value })}
                  required
                  minLength={3}
                />
              </Field>
              <div className="shrink">
                <button className="danger" type="submit" disabled={endMutation.isPending}>
                  End assignment
                </button>
              </div>
            </div>
          </form>
        </section>
      )}
    </>
  )
}
